import * as v2 from '@/core/services/api/v2';

/**
 * O intervalo do tique de energia no servidor (`INTERVALO_DO_TICK_MS` em
 * `packages/domain/src/energia.ts`). Aqui ele é só o teto da espera: quem
 * manda na hora da próxima leitura é o `proximaRecargaEm` que vem no perfil.
 */
const INTERVALO_DA_ENERGIA_MS = 5 * 60 * 1000;

/** Handles do relógio da energia. Fora do `state` de propósito: são recursos do navegador, não dados do jogador — o Vuex não deveria reagir a eles. */
let relogioDaEnergia = null;
let aoVoltarParaAba = null;

function pararRelogio(manterOuvinte = false) {
  if (relogioDaEnergia) {
    clearTimeout(relogioDaEnergia);
    relogioDaEnergia = null;
  }
  if (!manterOuvinte && aoVoltarParaAba) {
    document.removeEventListener('visibilitychange', aoVoltarParaAba);
    aoVoltarParaAba = null;
  }
}

const state = {
  id: null, // Add player ID to state
  experience: 0,
  level: 1,
  fishCaught: 0,
  totalFishCaught: 0,
  gold: 100, // valor inicial de gold
  paralleliums: 0, // Add this line
  purchasedMaps: [], // Add this line
  cardumeLendario: null, // Olhar de Águia (1.3): nome do mítico no mapa atual, ou nulo
  selectedBait: null,
  inventory: {
    fish: [],
    rods: [],
    baits: [],
    boats: [],
    houses: [],
    licenses: []
  },
  equipment: {
    rod: null,  // Currently equipped rod
    boat: null,  // Currently equipped boat
    house: null  // Currently equipped house
  },
  stats: {
    fishingPower: 0,    // Base fishing power
    catchRate: 0.5,     // Base catch rate (50%)
    criticalRate: 0.05,  // Base critical rate (5%)
    reelSpeed: 1,       // Base reel speed
  },
  currentMap: 12,
  stamina: {
    current: 0,
    max: 30,
    nextRecharge: 0,
    rechargeStamina: 1
  },
  maxExperience: 200, // Add this line after experience
  tickets: 0,
  richestPlayers: [],
  highestLevelPlayers: [],
  mostActiveFishers: [],
  identifier: "",
  taxes: false,
  dailyReward: false,
  firstFishOfDay: false,
  clan: null,
  clan_invites: [],
  clanCoins: 0,
  waiting_clans: [],
  claimedDailyMissionReward: false,
};

const mutations = {
  INCREMENT_EXPERIENCE(state, amount) {
    state.experience += amount;
    // Remove level up logic since it's handled by server
  },
  /**
   * A barra de XP e a energia que a própria resposta da captura trouxe.
   *
   * A releitura do perfil depois do lance (`loadPlayerState`) junta seis GETs
   * num `Promise.all` e falha calada: bastava um cair para a barra de XP ficar
   * parada até recarregar a página (relato de 23/09/2026). Com isto, o que a
   * captura mudou chega à tela mesmo que a releitura falhe.
   */
  APLICAR_CAPTURA(state, { experience, maxExperience, level, staminaRemaining }) {
    if (Number.isFinite(experience)) state.experience = experience;
    if (Number.isFinite(maxExperience)) state.maxExperience = maxExperience;
    if (Number.isFinite(level)) state.level = level;
    if (Number.isFinite(staminaRemaining)) state.stamina.current = staminaRemaining;
  },
  CATCH_FISH(state) {
    state.fishCaught++;
    state.totalFishCaught++;
  },
  RESET_FISH_CAUGHT(state) {
    state.fishCaught = 0;
  },
  ADD_GOLD(state, amount) {
    state.gold += amount;
  },
  REMOVE_GOLD(state, amount) {
    if (state.gold >= amount) {
      state.gold -= amount;
    }
  },
  // Add a mutation to update clan coins
  SET_CLAN_COINS(state, amount) {
    state.clanCoins = amount;
  },
  // Add a mutation to add clan coins
  ADD_CLAN_COINS(state, amount) {
    state.clanCoins += amount;
  },
  DEDUCT_GOLD(state, amount) {
    if (state.gold >= amount) {
      state.gold -= amount;
    }
  },
  ADD_PARALLELIUMS(state, amount) {
    state.paralleliums += amount;
  },
  REMOVE_PARALLELIUMS(state, amount) {
    if (state.paralleliums >= amount) {
      state.paralleliums -= amount;
    }
  },
  /**
   * Mutation pura, como toda mutation deveria ser: quem fala com o servidor é a
   * action `purchaseMap`. A versão antiga fazia HTTP aqui dentro — e recebia o
   * **nome** do mapa; agora `purchasedMaps` guarda **ids**, que é o que o V2
   * devolve em `GET /player/:id/maps`.
   */
  ADD_PURCHASED_MAP(state, mapId) {
    if (!state.purchasedMaps) {
      state.purchasedMaps = [];
    }
    if (!state.purchasedMaps.includes(mapId)) {
      state.purchasedMaps.push(mapId);
    }
  },
  ADD_TO_INVENTORY(state, { category, item }) {
    // Garante que a categoria existe
    if (!state.inventory[category]) {
      state.inventory[category] = [];
    }
    
    // Clone o item para evitar referências
    const newItem = { ...item };
    
    // Adiciona o item ao inventário
    state.inventory[category].push(newItem);
    
    // Auto-equip para barcos
    if (category === 'boats' && state.inventory.boats.length === 1) {
      state.equipment.boat = newItem;
    }
  },
  REMOVE_FROM_INVENTORY(state, { category, index }) {
    state.inventory[category].splice(index, 1);
  },
  PURCHASE_ITEM(state, { category, item, quantity }) {
    const totalCost = item.price * quantity;
    if (state.gold >= totalCost) {
      state.gold -= totalCost;
      
      // Procura o item no inventário
      const existingItemIndex = state.inventory[category].findIndex(
        i => i.name === item.name
      );

      if (existingItemIndex !== -1 && item.stackable) {
        // Atualiza a quantidade se o item já existe
        const currentItem = state.inventory[category][existingItemIndex];
        currentItem.quantity = (currentItem.quantity || 1) + quantity;
      } else {
        // Adiciona novo item com quantidade
        state.inventory[category].push({
          ...item,
          quantity: quantity
        });
      }
    }
  },
  USE_BAIT(state) {
    if (state.inventory.baits.length > 0) {
      const bait = state.inventory.baits[0];
      if (bait.quantity > 1) {
        bait.quantity--;
      } else {
        state.inventory.baits.splice(0, 1);
      }
      return true;
    }
    return false;
  },
  UPDATE_INVENTORY(state, items) {
    const callId = 'UPDATE_INV_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
    console.log(`=== UPDATE_INVENTORY MUTATION [${callId}] ===`);
    console.log('Old inventory fish count:', state.inventory?.fish?.length || 0);
    console.log('New inventory fish count:', items?.fish?.length || 0);
    console.log('Stack trace:', new Error().stack);
    
    state.inventory = items;
    console.log('State updated. Current fish count:', state.inventory?.fish?.length || 0);
  },
  UPDATE_BAIT_QUANTITY(state, { baitId, quantity }) {
    const bait = state.inventory.baits.find(b => b.id === baitId);
    if (bait) {
      bait.quantity += quantity;
      if (bait.quantity <= 0) {
        state.inventory.baits = state.inventory.baits.filter(b => b.id !== baitId);
      }
    }
  },
  SELECT_BAIT(state, baitId) {
    state.selectedBait = baitId;
  },
  ADD_BAIT(state, bait) {
    const existingBait = state.inventory.baits.find(b => b.id === bait.id);
    if (existingBait) {
      existingBait.quantity += bait.quantity;
    } else {
      state.inventory.baits.push(bait);
    }
  },
  CONSUME_BAIT(state) {
    if (state.selectedBait) {
      const baitIndex = state.inventory.baits.findIndex(b => b.id === state.selectedBait.id);
      if (baitIndex !== -1) {
        const bait = state.inventory.baits[baitIndex];
        if (bait.quantity > 0) {
          bait.quantity--;
          // Remove a isca do inventário se a quantidade chegar a zero
          if (bait.quantity === 0) {
            state.inventory.baits.splice(baitIndex, 1);
            state.selectedBait = null;
          }
        }
      }
    }
  },
  DECREMENT_BAIT_QUANTITY(state, baitId) {
    const bait = state.inventory.baits.find(b => b.id === baitId);
    if (bait && bait.quantity > 0) {
      bait.quantity--;
      if (bait.quantity === 0) {
        state.inventory.baits = state.inventory.baits.filter(b => b.id !== baitId);
        state.selectedBait = null;
      }
    }
  },
  setInventory(state, inventory) {
    state.inventory = inventory;
  },
  setGold(state, amount) {
    state.gold = amount;
  },
  ADD_FISH_TO_INVENTORY(state, fish) {
    if (!state.inventory.fish) {
      state.inventory.fish = [];
    }
    state.inventory.fish.push(fish);
  },
  REMOVE_FISH_FROM_INVENTORY(state, fishId) {
    state.inventory.fish = state.inventory.fish.filter(f => f.id !== fishId);
  },
  EQUIP_ROD(state, rod) {
    state.equipment.rod = {...rod}; // Ensure we create a new object
  },
  
  EQUIP_BOAT(state, boat) {
    state.equipment.boat = boat;
  },
  SET_PLAYER_STATE(state, playerData) {
    console.log('[DEBUG] 🔄 SET_PLAYER_STATE mutation called');
    console.log('[DEBUG] Current inventory fish:', state.inventory.fish);
    console.log('[DEBUG] New inventory fish:', playerData.inventory?.fish);
    
    // Add safety checks and default values
    Object.assign(state, {
      id: playerData.id,
      experience: playerData.experience || 0,
      level: playerData.level || 1,
      fishCaught: playerData.fishCaught || 0,
      totalFishCaught: playerData.totalFishCaught || 0,
      gold: playerData.coins || playerData.gold || 0, // Handle both coins and gold fields
      paralleliums: playerData.paralleliums || 0,
      purchasedMaps: playerData.purchasedMaps || [],
      inventory: {
        fish: playerData.inventory.fish || [],
        rods: playerData.inventory.rods || [],
        baits: playerData.inventory.baits || [],
        boats: playerData.inventory.boats || [],
        houses: playerData.inventory.houses || [],
        licenses: playerData.inventory.licenses || [],
        others: playerData.inventory.others || []
      },
      equipment: playerData.equipment || {
        rod: null,
        boat: null,
        house: null
      },
      stats: {
        fishingPower: playerData.fishingPower || 0,
        catchRate: playerData.catchRate || 0.5,
        criticalRate: playerData.criticalRate || 0.05,
        reelSpeed: playerData.reelSpeed || 1
      },
      currentMap: playerData.currentMap?.name || playerData.currentMap || 12,
      cardumeLendario: playerData.cardumeLendario ?? null,
      powerUps: playerData.powerUps || [],
      stamina: {
        current: playerData.currentStamina || 0,
        max: playerData.maxStamina || 30,
        // Segundos até o próximo ponto, do servidor. O nome antigo
        // (`lastStaminaRecharge`) guardava a data do último tique e ninguém
        // sabia o que fazer com ela; fica como fallback para chamadas velhas.
        nextRecharge: playerData.nextRecharge ?? playerData.lastStaminaRecharge ?? 0,
        nextRechargeAt: playerData.nextRecharge ? Date.now() + playerData.nextRecharge * 1000 : 0,
        rechargeStamina: playerData.rechargeStamina || 0
      },
      maxExperience: playerData.maxExperience || calculateMaxExperience(playerData.level || 1),
      tickets: playerData.tickets || 0,
      isRefrigerator: playerData.isRefrigerator || false,
      moreRest: playerData.moreRest || false,
      hasCalendar: playerData.hasCalendar || false,
      identifier: playerData.identifier || "",
      dailyReward: playerData.dailyReward || false,
      taxes: playerData.taxes || null,
      firstFishOfDay: playerData.firstFishOfDay || false,
      clan: playerData.clan || null,
      clan_invites: playerData.clan_invites || [],
      clanCoins: playerData.clanCoins || 0,
      waiting_clans: playerData.waiting_clans || [],
      claimedDailyMissionReward: playerData.claimedDailyMissionReward || false
    });
  },
  UPDATE_STATS(state, stats) {
    state.stats = stats;
  },
  SET_CURRENT_MAP(state, mapId) {
    state.currentMap = mapId;
  },
  SET_PLAYER_ID(state, id) {
    state.id = id;
  },
  SET_EQUIPMENT(state, equipment) {
    state.equipment = equipment;
  },
  SET_ROD(state, rod) {
    state.equipment.rod = rod;
  },
  SET_BOAT(state, boat) {
    state.equipment.boat = boat;
  },
  SET_INVENTORY(state, inventory) {
    const callId = 'INVENTORY_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
    console.log(`=== SET_INVENTORY MUTATION [${callId}] ===`);
    console.log('Old inventory fish count:', state.inventory?.fish?.length || 0);
    console.log('New inventory fish count:', inventory?.fish?.length || 0);
    
    // Full stack trace to see who's calling this
    const stack = new Error().stack;
    console.log('Stack trace:', stack);
    
    // Check if this looks like a reload from server
    if (inventory?.fish?.length > state.inventory?.fish?.length) {
      console.warn('⚠️ INVENTORY INCREASED - POTENTIAL RELOAD FROM SERVER');
      console.warn('This might be overriding a recent sale');
    }
    
    state.inventory = inventory;
    console.log('State updated. Current fish count:', state.inventory?.fish?.length || 0);
  },
  SET_STAMINA(state, { current, max, nextRecharge, rechargeStamina }) {
    state.stamina = {
      current,
      max,
      nextRecharge,
      rechargeStamina,
      // O instante do próximo ponto, para o relógio do HUD contar para trás.
      // `nextRecharge` são segundos NA HORA DA LEITURA; sem o carimbo o número
      // ficaria parado até a próxima releitura.
      nextRechargeAt: nextRecharge ? Date.now() + nextRecharge * 1000 : 0,
    };
  },
  SET_CURRENT_STAMINA(state, current) {
    state.stamina.current = current;
  },
  REMOVE_TICKET(state) {
    if (state.tickets > 0) {
      state.tickets--;
    }
  },

  ADD_TICKET(state) {
    state.tickets++;
  },
  SET_TICKETS(state, amount) {
    state.tickets = amount;
  },
  UPDATE_PLAYER_DATA(state, playerData) {
    if (playerData.inventory) {
      state.inventory = typeof playerData.inventory === 'string' 
        ? JSON.parse(playerData.inventory) 
        : playerData.inventory;
    }
    if (playerData.gold) {
      state.gold = playerData.gold;
    }
    if (playerData.paralleliums) {
      state.paralleliums = playerData.paralleliums;
    }
  },
  SET_GOLD(state, gold) {
    console.log('=== SET_GOLD MUTATION ===');
    console.log('Old gold:', state.gold);
    console.log('New gold:', gold);
    state.gold = gold;
    console.log('State updated. Current gold:', state.gold);
  },
  UPDATE_CURRENT_STAMINA(state, newStamina) {
    state.stamina.current = newStamina;
  },
  SET_RICHEST_PLAYERS(state, players) {
    state.richestPlayers = players;
  },
  SET_HIGHEST_LEVEL_PLAYERS(state, players) {
    state.highestLevelPlayers = players;
  },
  SET_MOST_ACTIVE_FISHERS(state, players) {
    state.mostActiveFishers = players;
  },
  SET_TAXES_STATUS(state, status) {
    state.taxes = status;
  },
  SET_DAILY_REWARD_STATUS(state, status) {
    state.dailyReward = status;
  },
  SET_FIRST_FISH_DAY(state, status) {
    state.firstFishOfDay = status;
  },
  UPDATE_PLAYER_CLAN(state, clan) {
    state.clan = clan;
  },
  SET_CLAIMED_DAILY_MISSION_REWARD(state, status) {
    state.claimedDailyMissionReward = status;
  },
};

/**
 * A pilha do inventário que o jogador **tem** para um tônico ou baú, por id de
 * catálogo ou por nome.
 *
 * O V2 consome por id de catálogo, e o mesmo tônico existe em várias linhas do
 * catálogo: o avulso (id 4) e os lotes de 10 e 25 (ids 20 e 25) têm o mesmo
 * nome. Até 23/09/2026 isto procurava na **vitrine da loja** pelo nome, e
 * duas coisas quebravam: o nome casava sempre com o avulso, e quem só tinha o
 * lote recebia "você não tem esse tônico"; e quem não tinha aberto a loja na
 * sessão tinha a vitrine vazia, e nenhum tônico nem baú funcionava.
 *
 * Por isso procura no inventário: o id exato primeiro, depois qualquer pilha
 * de mesmo nome com quantidade — lote e avulso fazem a mesma coisa.
 */
function resolverItemPremium(state, rootState, nomeOuId) {
  const guardados = (state.inventory?.others || []).filter((i) => (i.quantity ?? 0) > 0);
  const exato = guardados.find((i) => i.id === nomeOuId);
  if (exato) return exato;

  const catalogo = rootState.shop?.premiumItems || [];
  const nome =
    typeof nomeOuId === 'string'
      ? nomeOuId
      : catalogo.find((i) => i.id === nomeOuId)?.name;
  const mesmoNome = nome && guardados.find((i) => i.name === nome);
  if (mesmoNome) return mesmoNome;

  throw new Error(`Item premium não encontrado no inventário: ${nomeOuId}`);
}

// Add helper function
function calculateMaxExperience(level) {
  const baseExp = 200;
  const baseGrowthRate = 1.15;
  const scalingTiers = [
    { level: 1, factor: 1.0 },
    { level: 10, factor: 1.2 },
    { level: 20, factor: 1.4 },
    { level: 30, factor: 1.6 },
    { level: 40, factor: 1.8 },
    { level: 50, factor: 2.0 },
    { level: 60, factor: 2.3 },
    { level: 70, factor: 2.5 }
  ];

  const currentTier = scalingTiers.reduce((prev, curr) => 
    level >= curr.level ? curr : prev
  );

  let experience = baseExp * Math.pow(baseGrowthRate, level - 1);
  experience *= currentTier.factor;

  if (level === 70) {
    experience = 1000000;
  } else if (level > 70) {
    experience = 1000000 * Math.pow(1.5, level - 70);
  }

  return Math.floor(experience);
}

const actions = {
  /**
   * Carrega o jogador do backend V2.
   *
   * O parâmetro passou a ser o `playerId` (`players.id`), e não o id do usuário:
   * é assim que as rotas do V2 são endereçadas, e quem descobre esse número é
   * `GET /api/auth/me`, no store de autenticação.
   *
   * Não existe mais "find or create". No V2 o jogador nasce junto com a conta,
   * na mesma transação — uma conta sem jogador deixou de ser um estado
   * alcançável, e por isso o `referralId` foi para o cadastro.
   */
  async initializePlayer({ commit }, playerId) {
    try {
      const playerData = await v2.carregarJogador(playerId);
      commit('SET_PLAYER_STATE', playerData);
      commit('SET_TAXES_STATUS', playerData.taxes);
      commit('SET_DAILY_REWARD_STATUS', playerData.dailyReward);
      commit('SET_CLAIMED_DAILY_MISSION_REWARD', playerData.claimedDailyMissionReward || false);
      return playerData;
    } catch (error) {
      console.error('Error initializing player:', error);
      throw error;
    }
  },

  /**
   * Vender peixe.
   *
   * Sobem só os **ids** das linhas do inventário. O preço é do servidor: no
   * Strapi o corpo levava `price`, e o `secureApiClient` existia justamente para
   * assinar o que o cliente afirmava valer. Com o preço calculado do outro lado,
   * não sobra nada para assinar — por isso ele saiu daqui.
   *
   * O inventário local é recortado em vez de recarregado: o servidor já disse
   * quantos venderam e qual o saldo, e uma ida a mais ao banco por venda é
   * exatamente o tipo de coisa que derrubava a V1.
   */
  async sellFish({ commit, state }, { playerId, fish }) {
    if (!playerId) {
      throw new Error('Player ID is required');
    }

    const peixes = Array.isArray(fish) ? fish : [fish];
    const ids = peixes.map((p) => (typeof p === 'object' ? p.id : p)).filter(Boolean);

    if (!ids.length) {
      throw new Error('Nenhum peixe selecionado para venda');
    }

    try {
      const resultado = await v2.vender(playerId, ids);

      const vendidos = new Set(ids);
      commit('SET_INVENTORY', {
        ...state.inventory,
        fish: (state.inventory.fish || []).filter((p) => !vendidos.has(p.id)),
      });
      commit('SET_GOLD', resultado.gold);

      return { success: true, ...resultado };
    } catch (error) {
      console.error('Error selling fish:', error);
      throw error;
    }
  },

  /**
   * Equipar no V2, para as três categorias.
   *
   * `gearId` é o id da **instância** (`player_gear.id`) — vem de
   * `state.inventory.rods[..].id`, que agora é a lista do baú do V2. O servidor
   * devolve o perfil inteiro com os atributos recalculados; o inventário local
   * é preservado (a rota de perfil não fala de inventário) e a lista da
   * categoria é remarcada aqui, porque só o cliente sabe qual instância acabou
   * de pedir.
   */
  async equiparPeca({ state, commit }, { gearId, category }) {
    if (!state.id) {
      throw new Error('Player ID not available');
    }
    const playerData = await v2.equipar(state.id, gearId);
    playerData.inventory = {
      ...state.inventory,
      [category]: (state.inventory[category] || []).map((peca) => ({
        ...peca,
        equipped: peca.id === gearId,
      })),
    };
    playerData.purchasedMaps = state.purchasedMaps;
    commit('SET_PLAYER_STATE', playerData);
    return playerData;
  },

  async equipRod({ dispatch }, { rodId }) {
    try {
      return await dispatch('equiparPeca', { gearId: rodId, category: 'rods' });
    } catch (error) {
      console.error('Error equipping rod:', error);
      throw error;
    }
  },

  async equipBoat({ dispatch }, { boatId }) {
    try {
      return await dispatch('equiparPeca', { gearId: boatId, category: 'boats' });
    } catch (error) {
      console.error('Error equipping boat:', error);
      throw error;
    }
  },

  async equipHouse({ dispatch }, { houseId }) {
    try {
      return await dispatch('equiparPeca', { gearId: houseId, category: 'houses' });
    } catch (error) {
      console.error('Error equipping house:', error);
      throw error;
    }
  },

  /** Desequipar um slot (`rod`, `boat`, `house`). O espelho de `equiparPeca`. */
  async unequipSlot({ state, commit }, slot) {
    if (!state.id) {
      throw new Error('Player ID not available');
    }
    const category = { rod: 'rods', boat: 'boats', house: 'houses' }[slot];
    const playerData = await v2.desequipar(state.id, slot);
    playerData.inventory = {
      ...state.inventory,
      [category]: (state.inventory[category] || []).map((peca) => ({
        ...peca,
        equipped: false,
      })),
    };
    playerData.purchasedMaps = state.purchasedMaps;
    commit('SET_PLAYER_STATE', playerData);
    return playerData;
  },

  resetFishCaught({ commit }) {
    commit('RESET_FISH_CAUGHT');
  },

  initializeInventory({ commit }) {
    const savedState = localStorage.getItem('gameState');
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      commit('UPDATE_INVENTORY', parsedState.inventory);
      commit('ADD_GOLD', parsedState.gold - state.gold);
    } else {
      commit('setInventory', {
        rods: [],
        baits: [],
        boats: [],
        items: []
      });
    }
  },

  // Auto-equip first usable rod if none is equipped
  async autoEquipRod({ state, dispatch }) {
    try {
      if (!state.id) {
        console.warn('No player ID available for auto-equip');
        return;
      }
      if (state.equipment?.rod) return;
      // Vara quebrada não conta: equipar uma com durabilidade zero só adia o erro.
      const rods = (state.inventory?.rods || []).filter(
        (rod) => (rod.durability?.current ?? 0) > 0,
      );
      if (rods.length > 0) {
        await dispatch('equipRod', { rodId: rods[0].id });
      }
    } catch (error) {
      console.error('Error in autoEquipRod:', error);
    }
  },

  // Auto-equip first available boat if none is equipped
  async autoEquipBoat({ dispatch, state }) {
    try {
      if (!state.equipment.boat && state.inventory.boats.length > 0) {
        await dispatch('equipBoat', { boatId: state.inventory.boats[0].id });
      }
    } catch (error) {
      console.error('Error in autoEquipBoat:', error);
    }
  },

  addParalleliums({ commit }, amount) {
    commit('ADD_PARALLELIUMS', amount);
  },

  useParalleliums({ commit, state }, amount) {
    if (state.paralleliums >= amount) {
      commit('REMOVE_PARALLELIUMS', amount);
      return true;
    }
    return false;
  },


  /**
   * Recarrega perfil e inventário do V2.
   *
   * `playerId`, não id de usuário — ver `initializePlayer`. É chamado depois de
   * cada pescaria para trazer energia, XP e nível já recalculados pelo servidor.
   */
  async loadPlayerState({ commit }, playerId) {
    try {
      const playerData = await v2.carregarJogador(playerId);
      if (playerData) {
        commit('SET_PLAYER_STATE', playerData);
        // Explicitly set the claimed reward status to ensure it's properly tracked
        commit('SET_CLAIMED_DAILY_MISSION_REWARD', playerData.claimedDailyMissionReward || false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading player state:', error);
      // O lote é seis GETs num `Promise.all`: um que caia derruba todos. O
      // perfil sozinho ainda traz o que mais muda a cada lance — sem isto a
      // barra de XP ficava parada até recarregar a página (23/09/2026).
      try {
        const perfil = await v2.perfil(playerId);
        commit('APLICAR_CAPTURA', {
          experience: perfil.experiencia,
          maxExperience: perfil.maxExperiencia,
          level: perfil.nivel,
          staminaRemaining: perfil.staminaAtual,
        });
        commit('SET_GOLD', perfil.gold);
      } catch (erroDoPerfil) {
        console.error('Error loading player profile:', erroDoPerfil);
      }
      return false;
    }
  },

  /**
   * Trocar de mapa, no V2 — por **id**, não mais por nome: `current_map_id` é
   * FK. Os requisitos (mapa liberado, nível, barco equipado) são conferidos no
   * servidor; a versão antiga escrevia o nome que o próprio cliente mandava.
   * `SET_CURRENT_MAP` continua guardando o nome, que é o que as telas comparam.
   */
  async changeCurrentMap({ commit, state }, mapId) {
    try {
      if (!state.id) throw new Error('No player ID found');

      const resultado = await v2.trocarMapa(state.id, mapId);
      commit('SET_CURRENT_MAP', resultado.name);
      return true;
    } catch (error) {
      console.error('Error changing map:', error);
      throw error;
    }
  },

  /**
   * Liberar (comprar) um mapa, no V2. O preço e os requisitos são do servidor;
   * o ouro local é o que a resposta disse que sobrou — a versão antiga fazia
   * `DEDUCT_GOLD` por conta própria, com o preço que a tela conhecia.
   */
  async purchaseMap({ commit, state }, mapId) {
    if (!state.id) throw new Error('No player ID found');

    const resultado = await v2.liberarMapa(state.id, mapId);
    commit('ADD_PURCHASED_MAP', resultado.mapId);
    commit('SET_GOLD', resultado.gold);
    return resultado;
  },

  /** Recarrega tudo do V2 — as chamadas soltas ao Strapi morreram aqui. */
  async updateInventory({ dispatch, state }) {
    if (!state.id) {
      console.warn('No player ID available for inventory update');
      return;
    }
    await dispatch('loadPlayerState', state.id);
  },

  async updateGold({ commit, state }) {
    try {
      const perfil = await v2.perfil(state.id);
      commit('SET_GOLD', perfil.gold);
    } catch (error) {
      console.error('Error updating gold:', error);
    }
  },

  /**
   * Quem credita o pagamento é o **webhook** da Stripe — não existe mais o
   * `confirm` do Strapi. Confirmar aqui é esperar o crédito aparecer no
   * histórico (cartão leva segundos; boleto, dias — aí a tela mostra pendente).
   */
  async confirmPayment({ state, dispatch }, sessionId) {
    void sessionId; // O histórico não expõe o sessionId; o critério é o registro mais novo.
    try {
      const pacotes = await v2.pacotesDePagamento();
      for (let tentativa = 0; tentativa < 10; tentativa += 1) {
        const historico = await v2.historicoDePagamentos(state.id);
        const maisNovo = historico[0];
        if (maisNovo?.status === 'completed') {
          await dispatch('loadPlayerState', state.id);
          const pacote = pacotes.find((p) => p.id === maisNovo.pacoteId);
          const creditado = pacote?.paralleliums ?? 0;
          return { success: true, paralleliums: creditado, totalParalleliums: state.paralleliums };
        }
        await new Promise((resolver) => setTimeout(resolver, 2000));
      }
      return false;
    } catch (error) {
      console.error('Error confirming payment:', error);
      return false;
    }
  },

  /**
   * Relê a energia do servidor.
   *
   * Substitui a `rechargeStamina` do Strapi, que **pedia ao servidor que
   * recarregasse** — e o servidor obedecia, com o cliente decidindo a hora. No
   * V2 a regeneração é derivada do relógio na leitura: aqui só se lê.
   *
   * Devolve os segundos até o próximo ponto, que é o que o relógio usa para
   * saber quando voltar.
   */
  async atualizarEnergia({ commit, state }) {
    if (!state.id) return null;
    const perfil = await v2.perfil(state.id);
    commit('SET_STAMINA', {
      current: perfil.staminaAtual,
      max: perfil.staminaMaxima,
      nextRecharge: perfil.proximaRecargaEm ?? 0,
      rechargeStamina: state.stamina?.rechargeStamina ?? 1,
    });
    return perfil.proximaRecargaEm ?? 0;
  },

  /**
   * O relógio da energia.
   *
   * Sem ele a barra fica parada: a regeneração é preguiçosa no servidor, então
   * **nada chega sozinho** — quem não relê nunca vê o número subir. Foi o
   * defeito de 04/09/2026, achado com o jogo aberto a tarde toda em 5/100.
   *
   * Não é um `setInterval` de um minuto: o servidor diz quantos segundos faltam
   * para o próximo ponto, e o relógio dorme exatamente isso. Uma leitura a cada
   * cinco minutos em vez de cinco por minuto — e ela cai no instante em que o
   * número de fato muda.
   *
   * Aba escondida não lê nada — o jogo em segundo plano por horas seriam
   * dezenas de requisições para uma tela que ninguém está olhando. Ao voltar,
   * lê na hora: é justamente o caso em que a energia mais mudou.
   */
  iniciarRelogioDaEnergia({ dispatch, state }) {
    pararRelogio();

    const proximaLeitura = async () => {
      if (!state.id) return;
      // Aba escondida não lê: quem não está olhando não precisa do número, e
      // o ouvinte abaixo lê assim que a aba voltar. O relógio continua armado
      // para o caso de o evento de visibilidade não vir.
      if (document.visibilityState === 'hidden') {
        return agendar(INTERVALO_DA_ENERGIA_MS);
      }
      // Cheio não tem o que reler. O relógio continua armado porque o jogador
      // pode gastar energia a qualquer momento.
      if ((state.stamina?.current ?? 0) >= (state.stamina?.max ?? 0)) {
        return agendar(INTERVALO_DA_ENERGIA_MS);
      }
      try {
        const segundos = await dispatch('atualizarEnergia');
        agendar(((segundos ?? 0) + 1) * 1000);
      } catch (erro) {
        // Rede caída ou 401: tenta de novo no intervalo cheio, sem barulho.
        console.warn('Não consegui reler a energia:', erro?.message || erro);
        agendar(INTERVALO_DA_ENERGIA_MS);
      }
    };

    const agendar = (ms) => {
      relogioDaEnergia = setTimeout(
        proximaLeitura,
        // O piso de dez segundos é contra um servidor devolvendo zero, que
        // viraria uma requisição por milissegundo. O teto é o intervalo do
        // tique **mais a margem** — cortar em 300s exatos faria a leitura cair
        // um segundo antes do ponto e render uma segunda leitura logo atrás.
        Math.min(INTERVALO_DA_ENERGIA_MS + 10_000, Math.max(10_000, ms)),
      );
    };

    aoVoltarParaAba = () => {
      if (document.visibilityState !== 'visible') return;
      // Voltou para a aba: lê agora, não no horário que estava agendado.
      pararRelogio(true);
      proximaLeitura();
    };
    document.addEventListener('visibilitychange', aoVoltarParaAba);

    agendar(((state.stamina?.nextRecharge ?? 0) + 1) * 1000);
  },

  pararRelogioDaEnergia() {
    pararRelogio();
  },

  /**
   * Descansar em casa, no V2 (`POST /rest`). O flag antigo `isHouse` virou
   * `descansando` no servidor — e agora tem efeito de verdade: a regeneração
   * de energia corre três vezes mais rápido. Evento `noRest` recusa com 409.
   */
  async updateIsHouse({ state, commit }, isHouse) {
    try {
      if (!state.id) {
        throw new Error('Player ID is required');
      }
      const playerData = await v2.descansar(state.id, isHouse);
      commit('SET_CURRENT_STAMINA', playerData.currentStamina);
      return { data: { isHouse: playerData.isResting } };
    } catch (error) {
      console.error('Error updating isHouse:', error);
      throw error;
    }
  },

  async getIsHouse(_, playerId) {
    try {
      const perfil = await v2.perfil(playerId);
      return { data: { isHouse: perfil.descansando } };
    } catch (error) {
      console.error('Error getting isHouse status:', error);
      throw error;
    }
  },

  /**
   * Paga o imposto do dia, no V2. O valor é calculado no servidor
   * (`impostoDevido`); a tela só anima o número que voltou.
   */
  async chargeTaxes({ state, commit }) {
    if (!state.id) {
      throw new Error('Player ID is required');
    }
    const resultado = await v2.cobrarImpostos(state.id);
    commit('SET_GOLD', resultado.gold);
    commit('SET_TAXES_STATUS', false);
    return resultado;
  },

  /**
   * Recompensa diária, no V2 (`POST /daily-reward/claim`). A resposta é
   * `{ dia, tipo, valor, tickets }` — o envelope `{ success, reward, ticket }`
   * que o `App.vue` lê é refeito aqui. O `tickets` é o +1 da diária, como no
   * Strapi: o servidor credita e o estado local acompanha.
   */
  async claimDailyReward({ state, commit }) {
    try {
      if (!state.id) {
        throw new Error('Player ID not available');
      }
      const reward = await v2.resgatarDiaria(state.id);

      switch (reward.type) {
        case 'stamina':
          commit('SET_CURRENT_STAMINA', state.stamina.current + reward.amount);
          break;
        case 'gold':
          commit('ADD_GOLD', reward.amount);
          break;
        case 'paralleliums':
          commit('ADD_PARALLELIUMS', reward.amount);
          break;
        case 'experience':
          commit('INCREMENT_EXPERIENCE', reward.amount);
          break;
        case 'bait':
          // A isca entra no inventário do servidor; aparece no próximo load.
          break;
      }
      commit('SET_DAILY_REWARD_STATUS', true);
      const ticketsGanhos = reward.tickets || 0;
      commit('SET_TICKETS', (state.tickets || 0) + ticketsGanhos);

      return {
        success: true,
        reward: { type: reward.type, amount: reward.amount, day: reward.day, bait: null },
        ticket: { current: state.tickets || 0, added: ticketsGanhos },
      };
    } catch (error) {
      console.error('Error claiming daily reward:', error);
      throw error;
    }
  },

  /**
   * O `:itemId` do V2 é o id do **catálogo** premium; as telas herdadas mandam
   * o **nome** do item. A resolução nome→id fica aqui, num lugar só.
   */
  async useTonic({ state, dispatch, rootState }, tonicId) {
    try {
      const item = resolverItemPremium(state, rootState, tonicId);
      const resultado = await v2.usarTonico(state.id, item.id);
      await dispatch('loadPlayerState', state.id);
      return {
        success: true,
        effect: resultado.effect,
        value: resultado.value,
        tonicEffects: {
          energy: resultado.effect === 'energy' ? resultado.value : 0,
          fishingPower: resultado.effect === 'moreFishingPower' ? resultado.value : 0,
        },
      };
    } catch (error) {
      console.error('Error using tonic:', error);
      throw error;
    }
  },

  async openLootBox({ state, dispatch, rootState }, lootBoxId) {
    try {
      const item = resolverItemPremium(state, rootState, lootBoxId);
      const resultado = await v2.abrirBau(state.id, item.id);
      await dispatch('loadPlayerState', state.id);
      return { success: true, reward: resultado.reward };
    } catch (error) {
      console.error('Error opening lootbox:', error);
      throw error;
    }
  },

  /**
   * Rankings no V2 (`GET /rankings/players/:tipo`). Os três compartilham a
   * forma — `position`, `username` e os números — e o adapter já entrega em
   * inglês; sumiu o duplo `.data` do envelope do Strapi.
   */
  async fetchRichestPlayers({ commit }) {
    try {
      commit('SET_RICHEST_PLAYERS', await v2.rankingDeJogadores('richest'));
    } catch (error) {
      console.error('Error fetching richest players:', error);
    }
  },
  async fetchHighestLevelPlayers({ commit }) {
    try {
      commit('SET_HIGHEST_LEVEL_PLAYERS', await v2.rankingDeJogadores('levels'));
    } catch (error) {
      console.error('Error fetching highest level players:', error);
    }
  },
  async fetchMostActiveFishers({ commit }) {
    try {
      commit('SET_MOST_ACTIVE_FISHERS', await v2.rankingDeJogadores('fishers'));
    } catch (error) {
      console.error('Error fetching most active fishers:', error);
    }
  },
};

const getters = {
  getExperience: state => state.experience,
  getLevel: state => state.level,
  getFishCaught: state => state.fishCaught,
  getTotalFishCaught: state => state.totalFishCaught,
  getGold: state => state.gold,
  getInventory: state => state.inventory,
  hasBait: state => state.inventory.baits.length > 0 && state.inventory.baits[0].quantity > 0,
  getBaits: state => state.inventory.baits || [],
  getFish: state => state.inventory.fish || [],
  getRods: state => state.inventory.rods || [],
  getSelectedBait: (state) => {
    return state.selectedBait ? 
      state.inventory.baits.find(b => b.id === state.selectedBait) : 
      null;
  },
  hasBaits: (state) => {
    return state.inventory.baits.some(bait => bait.quantity > 0);
  },
  getEquippedRod: state => state.equipment.rod,
  getEquippedBoat: state => state.equipment.boat,
  getPlayerStats: state => state.stats,
  canFish: state => {
    return state.equipment.rod !== null && state.inventory.baits.some(b => b.quantity > 0);
  },
  getBoats: state => state.inventory.boats || [],
  getParalleliums: state => state.paralleliums,
  hasEnoughParalleliums: state => amount => state.paralleliums >= amount,
  stamina: state => state.stamina,
  getClanCoins: state => state.clanCoins // Add this getter
};

export default {
  namespaced: true, // Add this line to namespace the module
  actions,
  state,
  mutations,
  getters
};